import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';

import __noop from '@atlaskit/ds-lib/noop';

export type Heading = {
  id: string;
  value: string;
  depth: number;
};

const HeadingContext = React.createContext<{
  headings: Heading[];
  /**
   * Override headings.
   *
   * Can be used to dispatch list of headings which are unknown until runtime.
   * e.g. a dynamic React component which show/hides certain section headings.
   */
  setHeadingsOverride: Dispatch<SetStateAction<Heading[] | null>>;
  /**
   * Resets heading override.
   */
  resetHeadings: () => void;
}>({
  headings: [],
  setHeadingsOverride: __noop,
  resetHeadings: __noop,
});

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export default HeadingContext;

export const useHeadings = () => {
  return useContext(HeadingContext);
};

// eslint-disable-next-line @repo/internal/react/require-jsdoc
export const HeadingContextProvider = ({
  children,
  headings,
}: {
  children: React.ReactNode;
  headings?: Heading[];
}) => {
  const [headingsOverride, setHeadingsOverride] = useState<Heading[] | null>(
    null,
  );

  const resetHeadings = useCallback(() => setHeadingsOverride(null), []);

  return (
    <HeadingContext.Provider
      value={{
        headings: headingsOverride || headings || [],
        setHeadingsOverride,
        resetHeadings,
      }}
    >
      {children}
    </HeadingContext.Provider>
  );
};
